// ABOUTME: A soft cumulus-cloud shape (rounded base + bump circles) behind arbitrary content,
// ABOUTME: used to build Crossing's stacked-cloud reveal scenes (Issue #1946).

import type { ReactNode } from 'react';
import { Box } from '@mui/material';

interface CloudBlobProps {
  color: string;
  maxWidth: number;
  minWidth?: number;
  // A cloud can be drawn in two matching layers, so its fill can fade while what is written on it stays full: 'fill' is the
  // silhouette with its words hidden, 'content' is the words with no silhouette. Absent, it is drawn whole.
  layer?: 'fill' | 'content';
  children: ReactNode;
}

// Bump circles are percentage-sized relative to the same box as the base rectangle, so the
// silhouette holds its proportions regardless of how much text the base grows to fit.
//
// Deliberately sized by content (width:'fit-content'), NOT width:'100%' + mx:'auto' -- the latter
// looks identical when this is a lone block-level child, but breaks badly when a caller places it
// as a flex item alongside siblings (the arrow-flanked storm cloud): width:'100%' inflates
// the flex-basis used for space distribution to the FULL container width before max-width later
// clamps the rendered box, and mx:'auto' becomes a flex auto-margin that greedily consumes all
// remaining free space -- together these shove flex siblings (the arrow buttons) out to the far
// edges of the row, present in the DOM and clickable but nowhere near the cloud (bryan, manual-test
// + Playwright-verified: "no arrows" -- they were ~500px away, not invisible). Callers that need
// this centered as a lone child should wrap it in a `justifyContent:'center'` flex row instead of
// relying on this component to center itself.
export default function CloudBlob({ color, maxWidth, minWidth, layer, children }: CloudBlobProps) {
  const fill = layer === 'content' ? 'transparent' : color;
  return (
    <Box sx={{ position: 'relative', width: 'fit-content', maxWidth, minWidth }}>
      <Box sx={{ position: 'absolute', left: '-6%', top: '18%', width: '28%', height: '55%', borderRadius: '50%', backgroundColor: fill }} />
      <Box sx={{ position: 'absolute', right: '-6%', top: '15%', width: '26%', height: '58%', borderRadius: '50%', backgroundColor: fill }} />
      <Box sx={{ position: 'absolute', left: '8%', top: '-22%', width: '42%', height: '65%', borderRadius: '50%', backgroundColor: fill }} />
      <Box sx={{ position: 'absolute', right: '10%', top: '-18%', width: '40%', height: '62%', borderRadius: '50%', backgroundColor: fill }} />
      <Box sx={{ position: 'relative', backgroundColor: fill, borderRadius: '40px', px: { xs: 3, sm: 5 }, py: 3, zIndex: 1, visibility: layer === 'fill' ? 'hidden' : undefined }}>
        {children}
      </Box>
    </Box>
  );
}
